import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  useToast,
  Card,
  CardBody,
  Icon,
  InputGroup,
  Badge,
  Alert,
  AlertIcon,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  Tag,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FaRocket,
  FaUpload,
  FaPlay,
  FaFileArchive,
  FaCode,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaBrain,
  FaInfoCircle,
  FaSyncAlt,
} from 'react-icons/fa';
import apiService from '../services/api';

// Extra flags forwarded to the preflight endpoints. PyFR preflight only needs
// the ZIP, so this is empty by default; add backend-understood flags here.
const PREFLIGHT_FORM_OPTIONS = {};

const PREFLIGHT_CHECK_LABELS = {
  mesh: 'Mesh validation',
  config: 'pyfr.ini structure / version',
  cross: 'Config ↔ mesh cross-check',
  run: 'PyFR dry-run',
  extract: 'Archive extraction',
  pipeline: 'Validation pipeline',
};

const getPreflightCheckLabel = (name) => PREFLIGHT_CHECK_LABELS[name] || name;

const getSeverityColor = (severity) => {
  const colors = {
    critical: 'red',
    high: 'orange',
    moderate: 'yellow',
    low: 'blue',
  };
  return colors[severity?.toLowerCase()] || 'gray';
};

const formatCheckItem = (item) => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return String(item);
  const label = item.name ? `${item.name}: ` : '';
  return `${label}${item.message || ''}`.trim() || item.name || 'Check failed';
};

const PreflightStepCard = ({ step, pending = false }) => {
  if (pending) {
    return (
      <Card borderWidth="2px" borderColor="gray.200" borderStyle="dashed">
        <CardBody py={3}>
          <HStack justify="space-between">
            <HStack>
              <Spinner size="sm" color="blue.400" />
              <Text fontWeight="semibold" fontSize="sm">
                {getPreflightCheckLabel(step)}
              </Text>
            </HStack>
            <Badge colorScheme="gray" variant="outline">Running</Badge>
          </HStack>
        </CardBody>
      </Card>
    );
  }

  const passed = step.ok === true;
  const failed = step.ok === false;
  const borderColor = failed ? 'red.300' : passed ? 'green.300' : 'yellow.300';
  const iconColor = failed ? 'red.500' : passed ? 'green.500' : 'yellow.500';
  const icon = failed ? FaTimes : passed ? FaCheckCircle : FaExclamationTriangle;
  const badgeColor = failed ? 'red' : passed ? 'green' : 'yellow';
  const errors = step.errors ?? [];
  const warnings = step.warnings ?? [];
  const results = step.results ?? [];

  return (
    <Card borderWidth="2px" borderColor={borderColor}>
      <CardBody py={3}>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <HStack>
              <Icon as={icon} color={iconColor} />
              <Text fontWeight="semibold" fontSize="sm">
                {getPreflightCheckLabel(step.stage)}
              </Text>
            </HStack>
            <Badge colorScheme={badgeColor} variant="solid">
              {passed ? 'PASSED' : failed ? 'FAILED' : 'UNKNOWN'}
            </Badge>
          </HStack>
          {errors.length > 0 && (
            <List spacing={1}>
              {errors.map((e, j) => (
                <ListItem key={j} fontSize="xs" color="red.600">
                  <ListIcon as={FaTimes} color="red.500" />
                  {formatCheckItem(e)}
                </ListItem>
              ))}
            </List>
          )}
          {warnings.length > 0 && (
            <List spacing={1}>
              {warnings.map((w, j) => (
                <ListItem key={j} fontSize="xs" color="yellow.700">
                  <ListIcon as={FaExclamationTriangle} color="yellow.500" />
                  {formatCheckItem(w)}
                </ListItem>
              ))}
            </List>
          )}
          {results.length > 0 && (
            <Box as="details">
              <Box as="summary" fontSize="xs" color="gray.600" cursor="pointer">
                Show all checks ({results.length})
              </Box>
              <List spacing={1} mt={2}>
                {results.map((r, j) => (
                  <ListItem key={j} fontSize="xs" color={r.passed ? 'green.700' : 'gray.700'}>
                    <ListIcon
                      as={r.passed ? FaCheckCircle : FaExclamationTriangle}
                      color={r.passed ? 'green.500' : r.severity === 'warning' ? 'yellow.500' : 'red.500'}
                    />
                    {formatCheckItem(r)}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

/** Split SSE buffer on blank lines; returns unparsed remainder. */
const consumeSseFrames = (buffer, onEvent) => {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const remaining = parts.pop() ?? '';

  for (const frame of parts) {
    let eventName = null;
    let dataJson = null;

    for (const line of frame.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith(':')) continue;
      if (trimmed.startsWith('event:')) {
        eventName = trimmed.slice(6).trim();
      } else if (trimmed.startsWith('data:')) {
        dataJson = trimmed.slice(5).trim();
      }
    }

    if (!eventName || !dataJson) continue;

    try {
      onEvent(eventName, JSON.parse(dataJson));
    } catch (e) {
      console.warn('[preflight] invalid JSON in SSE frame:', dataJson, e);
    }
  }

  return remaining;
};

const StartSimulation = () => {
  const [formData, setFormData] = useState({
    jobName: '',
    jobDescription: '',
    simulationTool: 'pyfr',
    zipFile: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isConfigValidated, setIsConfigValidated] = useState(false);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [, setIsPurchaseSummaryLoading] = useState(true);

  // Preflight (streaming) state — wire format: start → stage (×N) → done | error
  const [preflightStages, setPreflightStages] = useState([]);
  const [preflightSteps, setPreflightSteps] = useState([]);
  const [preflightPassed, setPreflightPassed] = useState(false);
  const [preflightFailed, setPreflightFailed] = useState(false);
  const [preflightComplete, setPreflightComplete] = useState(false);
  const [preflightError, setPreflightError] = useState(null);
  const [isPreflightRunning, setIsPreflightRunning] = useState(false);
  const { isOpen: isPreflightWarningOpen, onOpen: onPreflightWarningOpen, onClose: onPreflightWarningClose } = useDisclosure();
  const preflightAbortRef = useRef(null);

  // AI recommendations state
  const recommendationsAbortRef = useRef(null);
  const [recommendationsResponse, setRecommendationsResponse] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState(null);

  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetchPurchaseSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPurchaseSummary = async () => {
    try {
      setIsPurchaseSummaryLoading(true);
      const summary = await apiService.getPurchaseSummary();
      setPurchaseSummary(summary);
    } catch (err) {
      console.error('Failed to fetch purchase summary:', err);
    } finally {
      setIsPurchaseSummaryLoading(false);
    }
  };

  const isPaidUser = purchaseSummary?.product_name === 'On Demand Usage';

  const validationFailed =
    preflightFailed
    || Boolean(preflightError)
    || (preflightComplete && !preflightPassed);

  const canStartSimulation =
    Boolean(formData.zipFile)
    && !isPreflightRunning
    && preflightComplete;

  const resetPreflightState = () => {
    setPreflightStages([]);
    setPreflightSteps([]);
    setPreflightPassed(false);
    setPreflightFailed(false);
    setPreflightComplete(false);
    setPreflightError(null);
    setIsConfigValidated(false);
  };

  const canGetRecommendations =
    isPaidUser
    && Boolean(formData.zipFile)
    && validationFailed
    && !isPreflightRunning
    && !recommendationsLoading;

  const resetRecommendationsState = () => {
    setRecommendationsResponse(null);
    setRecommendationsLoading(false);
    setRecommendationsError(null);
    if (recommendationsAbortRef.current) {
      recommendationsAbortRef.current.abort();
      recommendationsAbortRef.current = null;
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        setFormData((prev) => ({
          ...prev,
          zipFile: file,
        }));
        setFileName(file.name);
        resetRecommendationsState();
        resetPreflightState();
      } else {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload a ZIP file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = '';
      }
    }
  };

  const validateForm = () => {
    if (!formData.jobName.trim()) {
      toast({ title: 'Error', description: 'Please enter a job name.', status: 'error', duration: 3000, isClosable: true });
      return false;
    }

    if (!formData.jobDescription.trim()) {
      toast({ title: 'Error', description: 'Please enter a job description.', status: 'error', duration: 3000, isClosable: true });
      return false;
    }

    if (!formData.zipFile) {
      toast({ title: 'Error', description: 'Please upload a ZIP file.', status: 'error', duration: 3000, isClosable: true });
      return false;
    }

    if (!preflightComplete) {
      toast({
        title: 'Preflight required',
        description: preflightError
          ? 'Preflight failed before validation could complete. Fix the issue and re-run preflight.'
          : 'Please run preflight checks before starting the simulation.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    if (isPreflightRunning) {
      toast({
        title: 'Preflight in progress',
        description: 'Please wait for preflight checks to finish.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const submitSimulation = async () => {
    setIsLoading(true);

    try {
      const eligibility = await apiService.checkJobEligibility();

      if (!eligibility || !eligibility.is_eligible) {
        toast({
          title: 'Not Eligible to Start Simulation',
          description: eligibility?.reason || 'You are not eligible to start a simulation. Please check your credits.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('tool_name', formData.simulationTool);
      formDataToSend.append('zip_file', formData.zipFile);
      formDataToSend.append('job_name', formData.jobName);
      formDataToSend.append('job_desc', formData.jobDescription);

      const response = await apiService.startSimulation(formDataToSend);

      toast({
        title: 'Simulation Started!',
        description: `Job "${formData.jobName}" has been queued successfully. Job ID: ${response.job_id}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Simulation start error:', error);

      let errorMessage = 'An error occurred while starting the simulation.';

      if (error.message.includes('400')) {
        errorMessage = 'Please check your input and try again.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Please sign in to start simulations.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      }

      toast({
        title: 'Simulation Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmPreflightFailure = async () => {
    onPreflightWarningClose();
    await submitSimulation();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (preflightFailed && !preflightPassed) {
      onPreflightWarningOpen();
      return;
    }

    await submitSimulation();
  };

  const clearForm = () => {
    setFormData({
      jobName: '',
      jobDescription: '',
      simulationTool: 'pyfr',
      zipFile: null,
    });
    setFileName('');
    resetPreflightState();
    resetRecommendationsState();
    const fileInput = document.getElementById('zip-file-input');
    if (fileInput) fileInput.value = '';
  };

  // ----- Preflight streaming handler -----
  const handleRunPreflight = async () => {
    if (!formData.zipFile) {
      toast({
        title: 'ZIP file required',
        description: 'Please upload a ZIP file before running preflight checks.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Reset state for a fresh run
    resetPreflightState();
    resetRecommendationsState();
    setIsPreflightRunning(true);

    const formDataPayload = apiService.buildPreflightFormData(formData.zipFile, PREFLIGHT_FORM_OPTIONS);

    const controller = new AbortController();
    preflightAbortRef.current = controller;

    const handleSseEvent = (event, data) => {
      console.debug('[preflight]', event, data);

      switch (event) {
        case 'start':
          setPreflightPassed(false);
          setPreflightFailed(false);
          setPreflightComplete(false);
          setIsConfigValidated(false);
          setPreflightError(null);
          setPreflightStages(data.stages ?? []);
          setPreflightSteps([]);
          break;

        case 'stage':
          setPreflightSteps((prev) => {
            const idx = prev.findIndex((s) => s.stage === data.stage);
            if (idx === -1) return [...prev, data];
            const next = [...prev];
            next[idx] = data;
            return next;
          });
          break;

        case 'done':
          setPreflightComplete(true);
          setPreflightPassed(data.ok === true);
          setPreflightFailed(data.ok === false);
          setIsConfigValidated(data.ok === true);
          break;

        case 'error':
          setPreflightPassed(false);
          setPreflightFailed(true);
          setPreflightComplete(false);
          setIsConfigValidated(false);
          setPreflightError(data.message || 'Preflight error');
          break;

        default:
          console.warn('[preflight] unknown event:', event);
          break;
      }
    };

    try {
      const response = await apiService.validatePreflight(formDataPayload);

      if (!response.body || typeof response.body.getReader !== 'function') {
        const text = await response.text();
        consumeSseFrames(`${text}\n\n`, handleSseEvent);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = consumeSseFrames(buffer, handleSseEvent);
      }

      // Flush any trailing frame that didn't end with a blank line.
      buffer += decoder.decode();
      if (buffer.trim()) {
        consumeSseFrames(`${buffer}\n\n`, handleSseEvent);
      }
    } catch (err) {
      if (controller.signal.aborted) return; // user cancelled
      console.error('Preflight error:', err);
      const status = err.status;
      let title = 'Preflight failed';
      let description = err.message || 'An error occurred during preflight checks.';
      if (status === 403) {
        title = 'Paid plan required';
        description = 'Preflight checks are available for paid users only.';
      } else if (status === 413) {
        title = 'ZIP too large';
        description = 'Your ZIP file exceeds the upload limit.';
      } else if (status === 400) {
        title = 'Invalid upload';
        description = 'Please upload a valid .zip file.';
      }
      setPreflightError(description);
      setPreflightPassed(false);
      setPreflightFailed(true);
      toast({ title, description, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsPreflightRunning(false);
      preflightAbortRef.current = null;
    }
  };

  const handleGetRecommendations = async () => {
    if (!formData.zipFile) {
      toast({
        title: 'ZIP file required',
        description: 'Please upload a ZIP file before requesting recommendations.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!isPaidUser) {
      toast({
        title: 'Paid plan required',
        description: 'AI preflight recommendations are available on paid plans only.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!validationFailed) {
      toast({
        title: 'Preflight must fail first',
        description: 'AI recommendations are available only after preflight checks fail.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setRecommendationsLoading(true);
    setRecommendationsResponse(null);
    setRecommendationsError(null);

    const controller = new AbortController();
    recommendationsAbortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 20 * 60 * 1000);

    try {
      const data = await apiService.fetchPreflightRecommendations(
        formData.zipFile,
        PREFLIGHT_FORM_OPTIONS,
        controller.signal
      );
      setRecommendationsResponse(data);
      if (Array.isArray(data.stages)) {
        setPreflightStages(data.stages);
      }
      if (Array.isArray(data.steps)) {
        setPreflightSteps(data.steps);
      }
      if (data.ok === true) {
        setPreflightPassed(true);
        setPreflightFailed(false);
        setPreflightComplete(true);
        setIsConfigValidated(true);
        setRecommendationsResponse(null);
      } else if (data.ok === false) {
        setPreflightPassed(false);
        setPreflightFailed(true);
        setPreflightComplete(true);
      } else if (data.overall === 'PASSED') {
        // Legacy recommendations response shape
        setPreflightPassed(true);
        setPreflightFailed(false);
        setPreflightComplete(true);
        setIsConfigValidated(true);
        setRecommendationsResponse(null);
      }
    } catch (err) {
      if (controller.signal.aborted) {
        setRecommendationsError('Request timed out or was cancelled. Please try again.');
      } else {
        console.error('Preflight recommendations error:', err);
        setRecommendationsError(err.message || 'Failed to fetch preflight recommendations.');
      }
    } finally {
      clearTimeout(timeoutId);
      setRecommendationsLoading(false);
      recommendationsAbortRef.current = null;
    }
  };

  const handleCancelPreflight = () => {
    if (preflightAbortRef.current) {
      preflightAbortRef.current.abort();
      setIsPreflightRunning(false);
      toast({
        title: 'Preflight cancelled',
        description: 'The preflight check was cancelled.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const orderedStageViews = preflightStages.length > 0
    ? preflightStages.map((stageName) => {
        const completed = preflightSteps.find((s) => s.stage === stageName);
        return {
          stageName,
          completed,
          pending: !completed && isPreflightRunning,
        };
      })
    : preflightSteps.map((s) => ({
        stageName: s.stage,
        completed: s,
        pending: false,
      }));

  const showPreflightOutput =
    Boolean(formData.zipFile)
    || preflightStages.length > 0
    || preflightSteps.length > 0
    || isPreflightRunning
    || preflightError
    || preflightComplete
    || recommendationsLoading
    || recommendationsResponse;

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="2xl">
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <HStack
              spacing={1}
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: 'scale(0.98)' }}
            >
              <Image src="/simple-logo.png" alt="SRS Logo" boxSize={14} objectFit="contain" />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Heading size="lg">Start New Simulation</Heading>
            <Text color="gray.600">Upload your simulation files and configure job parameters</Text>
          </VStack>

          {/* Simulation Form */}
          <Card w="full" shadow="lg">
            <CardBody p={8}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  {/* Job Name */}
                  <FormControl isRequired>
                    <FormLabel>Job Name</FormLabel>
                    <Input
                      type="text"
                      placeholder="taylor-green-re1600 (lowercase, hyphens)"
                      value={formData.jobName}
                      onChange={(e) => handleInputChange('jobName', e.target.value)}
                      size="lg"
                    />
                  </FormControl>

                  {/* Job Description */}
                  <FormControl isRequired>
                    <FormLabel>Job Description</FormLabel>
                    <Textarea
                      placeholder="Describe what this PyFR simulation will do..."
                      value={formData.jobDescription}
                      onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                      size="lg"
                      rows={4}
                    />
                  </FormControl>

                  {/* Simulation Tool */}
                  <FormControl isRequired>
                    <FormLabel>Simulation Tool</FormLabel>
                    <Select
                      value={formData.simulationTool}
                      onChange={(e) => handleInputChange('simulationTool', e.target.value)}
                      size="lg"
                    >
                      <option value="pyfr">SRS solver (powered by PyFR)</option>
                    </Select>
                  </FormControl>

                  {/* File Upload */}
                  <FormControl isRequired>
                    <FormLabel>Step 1: Upload ZIP File</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        id="zip-file-input"
                        type="file"
                        accept=".zip"
                        onChange={handleFileChange}
                        placeholder="Select ZIP file"
                        _placeholder={{ color: 'gray.400' }}
                        sx={{
                          '::file-selector-button': {
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            backgroundColor: 'brand.500',
                            color: 'white',
                            cursor: 'pointer',
                            marginRight: '12px',
                            '&:hover': {
                              backgroundColor: 'brand.600',
                            },
                          },
                        }}
                      />
                    </InputGroup>
                    {fileName && (
                      <HStack mt={2} spacing={2}>
                        <Icon as={FaFileArchive} color="green.500" />
                        <Text fontSize="sm" color="green.600">{fileName}</Text>
                        <Badge colorScheme="green" size="sm">Ready</Badge>
                      </HStack>
                    )}
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Please upload a ZIP file containing your simulation data
                    </Text>
                  </FormControl>

                  {/* Preflight Validation Section */}
                  <Box w="full">
                    <HStack mb={3} spacing={2}>
                      <FormLabel m={0}>Step 2: Run Preflight Checks</FormLabel>
                      <Tooltip
                        hasArrow
                        placement="top"
                        borderRadius="md"
                        p={3}
                        label={
                          <Box fontSize="xs">
                            <Text fontWeight="semibold" mb={1}>Expected ZIP structure</Text>
                            <Box fontFamily="mono" whiteSpace="pre">
{`your-case.zip
├── pyfr.ini
└── mesh/
    └── model.msh`}
                            </Box>
                            <Text mt={2} color="whiteAlpha.800">
                              The config must be named pyfr.ini — the mesh path inside it just has to match.
                            </Text>
                          </Box>
                        }
                      >
                        <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'blue.500' }} cursor="help">
                          <Icon as={FaInfoCircle} />
                        </Box>
                      </Tooltip>
                    </HStack>
                    <Alert status="warning" borderRadius="md" mb={4}>
                      <AlertIcon />
                      <Box>
                        <Text fontWeight="semibold">Preflight checks</Text>
                        <Text fontSize="sm">
                          Enter your job name, description, and upload your ZIP file, then run preflight checks before starting the simulation.
                        </Text>
                      </Box>
                    </Alert>
                    <Button
                      variant="outline"
                      size="lg"
                      w="full"
                      onClick={isPreflightRunning ? handleCancelPreflight : handleRunPreflight}
                      leftIcon={isPreflightRunning ? <FaTimes /> : <FaCode />}
                      colorScheme={isPreflightRunning ? 'red' : 'blue'}
                      isDisabled={!formData.zipFile}
                      isLoading={isPreflightRunning}
                      loadingText="Running preflight…"
                    >
                      {isPreflightRunning ? 'Cancel Preflight' : 'Run Preflight Checks'}
                    </Button>
                    {isConfigValidated && (
                      <HStack mt={2} spacing={2}>
                        <Icon as={FaCheckCircle} color="green.500" />
                        <Text fontSize="sm" color="green.600">Preflight checks passed</Text>
                        <Badge colorScheme="green" size="sm">Validated</Badge>
                      </HStack>
                    )}
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {preflightPassed
                        ? 'Preflight passed. You can start your simulation.'
                        : preflightFailed || preflightError
                          ? 'Preflight checks failed. Fix the issues and re-run, or start anyway at your own risk.'
                          : isPreflightRunning
                            ? 'Running preflight checks…'
                            : 'Run preflight checks after providing your job name, description, and ZIP file.'}
                    </Text>

                    {/* Live preflight output */}
                    {showPreflightOutput && (
                      <VStack spacing={3} align="stretch" mt={3}>
                        {orderedStageViews.map(({ stageName, completed, pending }) => (
                          <PreflightStepCard
                            key={stageName}
                            step={completed ?? stageName}
                            pending={pending}
                          />
                        ))}

                        {isPreflightRunning && preflightStages.length === 0 && preflightSteps.length === 0 && (
                          <HStack justify="center" p={4}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="blue.600">Starting preflight…</Text>
                          </HStack>
                        )}

                        {preflightError && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <Text fontWeight="semibold" fontSize="sm">Preflight error</Text>
                              <Text fontSize="sm">{preflightError}</Text>
                            </Box>
                          </Alert>
                        )}

                        {preflightComplete && !preflightError && (
                          <Alert
                            status={preflightPassed ? 'success' : 'error'}
                            borderRadius="md"
                          >
                            <AlertIcon />
                            <Box>
                              <Text fontWeight="semibold" fontSize="sm">
                                Overall: {preflightPassed ? 'Passed' : 'Failed'}
                              </Text>
                              <Text fontSize="sm">
                                {preflightPassed
                                  ? 'All preflight checks passed. You can start your simulation.'
                                  : 'One or more preflight checks failed. Fix the issues above and re-run, or start anyway at your own risk.'}
                              </Text>
                            </Box>
                          </Alert>
                        )}

                        {validationFailed && !isPaidUser && (
                          <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <Text fontSize="sm" fontWeight="semibold">
                                AI recommendations — paid feature
                              </Text>
                              <Text fontSize="sm" mt={1}>
                                Preflight AI fix guidance is available on paid plans only.
                              </Text>
                              <Button
                                size="sm"
                                mt={3}
                                colorScheme="brand"
                                variant="outline"
                                onClick={() => navigate('/buy-credits')}
                              >
                                Buy credits
                              </Button>
                            </Box>
                          </Alert>
                        )}

                        {isPaidUser && recommendationsLoading && (
                          <HStack justify="center" p={4} bg="cyan.50" borderRadius="md">
                            <Spinner size="sm" color="cyan.500" />
                            <Text fontSize="sm" color="cyan.800">
                              Running preflight checks and generating recommendations…
                            </Text>
                          </HStack>
                        )}

                        {isPaidUser && (validationFailed || recommendationsLoading || recommendationsResponse) && (
                          <Box pt={1}>
                            <Button
                              leftIcon={<FaBrain />}
                              colorScheme="cyan"
                              variant="outline"
                              size="md"
                              w="full"
                              onClick={handleGetRecommendations}
                              isDisabled={!canGetRecommendations}
                              isLoading={recommendationsLoading}
                              loadingText="Diagnosing with AI…"
                            >
                              Diagnose with AI
                            </Button>
                            {validationFailed && (
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Re-runs preflight on your ZIP and returns AI fix guidance. This may take several minutes.
                              </Text>
                            )}
                          </Box>
                        )}

                        {isPaidUser && recommendationsResponse && validationFailed && (
                          <Box mt={2}>
                            {recommendationsResponse.recommendations?.ok === true && (
                              <VStack spacing={3} align="stretch">
                                {recommendationsResponse.recommendations.summary && (
                                  <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm" fontWeight="semibold">
                                      {recommendationsResponse.recommendations.summary}
                                    </Text>
                                  </Alert>
                                )}
                                {recommendationsResponse.recommendations.recommendations?.map((rec, i) => (
                                  <Card key={i} borderWidth="1px" borderColor={`${getSeverityColor(rec.severity)}.200`}>
                                    <CardBody py={4}>
                                      <VStack align="stretch" spacing={3}>
                                        <HStack flexWrap="wrap" gap={2}>
                                          <Badge colorScheme={getSeverityColor(rec.severity)}>
                                            {rec.severity?.toUpperCase()}
                                          </Badge>
                                          <Badge variant="outline">
                                            Level {rec.level} ({getPreflightCheckLabel(rec.check)})
                                          </Badge>
                                        </HStack>
                                        <Box>
                                          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Issue</Text>
                                          <Text fontSize="sm">{rec.issue}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" fontWeight="semibold" color="orange.600" mb={1}>Root cause</Text>
                                          <Text fontSize="sm" bg="orange.50" p={2} rounded="md">{rec.root_cause}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" fontWeight="semibold" color="green.600" mb={1}>Recommended fix</Text>
                                          <Text fontSize="sm" bg="green.50" p={2} rounded="md" whiteSpace="pre-wrap">
                                            {rec.recommended_fix}
                                          </Text>
                                        </Box>
                                        {rec.config_keys_affected?.length > 0 && (
                                          <Wrap spacing={2}>
                                            {rec.config_keys_affected.map((key) => (
                                              <WrapItem key={key}>
                                                <Tag size="sm" colorScheme="blue" variant="subtle">{key}</Tag>
                                              </WrapItem>
                                            ))}
                                          </Wrap>
                                        )}
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                ))}
                              </VStack>
                            )}

                            {recommendationsResponse.recommendations?.ok === false && (
                              <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <Box flex="1">
                                  <Text fontSize="sm">
                                    {recommendationsResponse.recommendations.message || 'Could not generate preflight recommendations.'}
                                  </Text>
                                  {recommendationsResponse.recommendations.detail && (
                                    <Text fontSize="xs" color="gray.600" mt={2} whiteSpace="pre-wrap">
                                      {recommendationsResponse.recommendations.detail}
                                    </Text>
                                  )}
                                  {recommendationsResponse.recommendations.retryable && (
                                    <Button
                                      size="sm"
                                      mt={3}
                                      leftIcon={<FaSyncAlt />}
                                      colorScheme="cyan"
                                      variant="outline"
                                      onClick={handleGetRecommendations}
                                      isLoading={recommendationsLoading}
                                    >
                                      Try again
                                    </Button>
                                  )}
                                </Box>
                              </Alert>
                            )}
                          </Box>
                        )}

                        {isPaidUser && recommendationsError && !recommendationsResponse && (
                          <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Box flex="1">
                              <Text fontSize="sm">{recommendationsError}</Text>
                              <Button
                                size="sm"
                                mt={3}
                                leftIcon={<FaSyncAlt />}
                                colorScheme="cyan"
                                variant="outline"
                                onClick={handleGetRecommendations}
                                isLoading={recommendationsLoading}
                              >
                                Try again
                              </Button>
                            </Box>
                          </Alert>
                        )}
                      </VStack>
                    )}
                  </Box>

                  {/* Info Alert */}
                  <Alert status="info" borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <Box w="full">
                      <Text fontWeight="semibold">Simulation Requirements</Text>
                      <Text fontSize="sm">
                        Your ZIP must contain a pyfr.ini configuration and the mesh it references.
                        Run preflight checks after uploading to verify your archive.
                      </Text>
                      <Box
                        mt={2}
                        p={3}
                        bg="white"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="blue.100"
                        fontFamily="mono"
                        fontSize="xs"
                        whiteSpace="pre"
                        color="gray.700"
                      >
{`your-case.zip
├── pyfr.ini
└── mesh/
    └── model.msh`}
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        The config must be named pyfr.ini — the mesh path inside it just has to match.
                      </Text>
                    </Box>
                  </Alert>

                  {/* Action Buttons */}
                  <HStack spacing={4} w="full">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      flex={1}
                      onClick={clearForm}
                      leftIcon={<FaUpload />}
                    >
                      Clear Form
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      flex={1}
                      isLoading={isLoading}
                      loadingText="Starting Simulation..."
                      leftIcon={<FaPlay />}
                      isDisabled={!canStartSimulation}
                    >
                      Start Simulation
                    </Button>
                  </HStack>
                </VStack>
              </form>
            </CardBody>
          </Card>

          {/* Back to Dashboard */}
          <Button
            variant="ghost"
            leftIcon={<FaRocket />}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </VStack>
      </Container>

      <Modal isOpen={isPreflightWarningOpen} onClose={onPreflightWarningClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaExclamationTriangle} color="orange.400" />
              <Text>Preflight checks failed</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="gray.600" lineHeight="1.7">
              One or more preflight checks did not pass. Starting the simulation now may waste compute
              credits or produce incorrect results. Are you sure you want to continue?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPreflightWarningClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              leftIcon={<FaPlay />}
              onClick={handleConfirmPreflightFailure}
              isLoading={isLoading}
              loadingText="Starting…"
            >
              Start Anyway
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StartSimulation;
